<template>
  <div 
    class="vertical-nav-menu"
    :class="{ 'collapsed': !isVerticalMenuActive && isMobile }"
  >
    <!-- Header -->
    <div class="nav-header">
      <div class="nav-header-content">
        <div class="brand-logo">
          <img src="@/assets/images/logo/logo.png" alt="Gescom" class="logo-img">
        </div>
        <h4 class="nav-header-title">Gescom Portal</h4>
      </div>
      <b-button
        v-if="!isVerticalMenuActive"
        variant="link"
        class="nav-toggle-btn"
        @click="toggleVerticalMenuActive"
      >
        <feather-icon icon="MenuIcon" size="20" />
      </b-button>
    </div>
    
    <!-- Navigation Content -->
    <div class="nav-content">
      <ul class="nav nav-pills nav-vertical">
        <li
          v-for="(item, index) in menuItems"
          :key="index"
          class="nav-item"
          :class="{ 'has-sub': item.children }"
        >
          <!-- Single Menu Item -->
          <b-link
            v-if="!item.children"
            :to="{ name: item.route }"
            class="nav-link"
            :class="{ active: isActiveRoute(item.route) }"
            @click="closeMenuOnMobile"
          >
            <feather-icon :icon="item.icon" size="16" class="nav-icon" />
            <span class="nav-text">{{ item.title }}</span>
          </b-link>
          
          <!-- Dropdown Menu Item -->
          <div v-else class="nav-group">
            <div 
              class="nav-group-header"
              :class="{ 'active': isGroupActive(item) }"
              @click="toggleGroup(index)"
            >
              <div class="nav-group-content">
                <feather-icon :icon="item.icon" size="16" class="nav-icon" />
                <span class="nav-text">{{ item.title }}</span>
              </div>
              <feather-icon 
                :icon="expandedGroups.includes(index) ? 'ChevronDownIcon' : 'ChevronRightIcon'" 
                size="14" 
                class="nav-arrow"
              />
            </div>
            
            <!-- Submenu -->
            <transition name="submenu">
              <ul 
                v-show="expandedGroups.includes(index)"
                class="nav nav-pills nav-vertical submenu"
              >
                <li
                  v-for="(child, childIndex) in item.children"
                  :key="childIndex"
                  class="nav-item"
                >
                  <b-link
                    :to="{ name: child.route }"
                    class="nav-link submenu-link"
                    :class="{ active: isActiveRoute(child.route) }"
                    @click="closeMenuOnMobile"
                  >
                    <feather-icon 
                      :icon="child.icon || 'CircleIcon'" 
                      size="12" 
                      class="submenu-icon" 
                    />
                    <span class="submenu-text">{{ child.title }}</span>
                  </b-link>
                </li>
              </ul>
            </transition>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import navigation from '@/navigation/vertical'
import { BButton } from 'bootstrap-vue'

export default {
  name: 'VerticalNavMenu',
  components: {
    BButton,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      default: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      menuItems: navigation,
      expandedGroups: [],
      isMobile: false,
    }
  },
  mounted() {
    // Auto-expand groups that contain the current route
    this.autoExpandActiveGroups()
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
    
    // Debug: Log menu items
    console.log('Menu items:', this.menuItems)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  watch: {
    '$route'() {
      this.autoExpandActiveGroups()
    },
  },
  methods: {
    isActiveRoute(routeName) {
      return this.$route.name === routeName
    },
    isGroupActive(item) {
      if (!item.children) return false
      return item.children.some(child => this.isActiveRoute(child.route))
    },
    toggleGroup(index) {
      const groupIndex = this.expandedGroups.indexOf(index)
      if (groupIndex > -1) {
        this.expandedGroups.splice(groupIndex, 1)
      } else {
        this.expandedGroups.push(index)
      }
    },
    autoExpandActiveGroups() {
      this.expandedGroups = []
      this.menuItems.forEach((item, index) => {
        if (item.children && this.isGroupActive(item)) {
          this.expandedGroups.push(index)
        }
      })
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth < 1200
      // En desktop, siempre mantener el menú visible
      if (!this.isMobile) {
        this.$emit('update:isVerticalMenuActive', true)
      }
    },
    closeMenuOnMobile() {
      // Close menu on mobile after navigation
      if (this.isMobile) {
        this.toggleVerticalMenuActive()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.vertical-nav-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 260px;
  height: 100vh;
  background: #fff;
  border-right: 1px solid #e9ecef;
  z-index: 1000;
  overflow-y: auto;
  transition: all 0.3s ease;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  
  &.collapsed {
    transform: translateX(-100%);
  }
  
  // En desktop, el menú siempre está visible
  @media (min-width: 1200px) {
    &.collapsed {
      transform: translateX(0);
    }
  }
  
  .nav-header {
    padding: 1rem;
    border-bottom: 1px solid #e9ecef;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    position: relative;
    
    .nav-header-content {
      display: flex;
      align-items: center;
      
      .brand-logo {
        margin-right: 0.75rem;
        
        .logo-img {
          width: 32px;
          height: 32px;
          border-radius: 6px;
        }
      }
      
      .nav-header-title {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
        color: white;
      }
    }
    
    .nav-toggle-btn {
      position: absolute;
      right: 0.5rem;
      top: 50%;
      transform: translateY(-50%);
      color: white;
      padding: 0.25rem;
      
      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }
  }
  
  .nav-content {
    padding: 0.5rem 0;
    
    .nav {
      list-style: none;
      padding: 0;
      margin: 0;
    }
    
    .nav-item {
      margin: 0;
      margin-bottom: 0.25rem;
      display: block;
      width: 100%;
      
      &.has-sub {
        .nav-group-header {
          cursor: pointer;
          user-select: none;
        }
      }
    }
    
    .nav-link {
      display: flex;
      align-items: center;
      padding: 0.5rem 1rem;
      color: #6c757d;
      text-decoration: none;
      transition: all 0.2s ease;
      border-radius: 0;
      position: relative;
      
      &:hover {
        background-color: #f8f9fa;
        color: #495057;
        text-decoration: none;
      }
      
      &.active {
        background-color: #007bff;
        color: #fff;
        font-weight: 500;
        
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 3px;
          background-color: #0056b3;
        }
      }
      
      .nav-icon {
        margin-right: 0.75rem;
        flex-shrink: 0;
      }
      
      .nav-text {
        flex: 1;
        font-size: 0.875rem;
      }
    }
    
    .nav-group {
      margin-bottom: 0.75rem;
      position: relative;
      clear: both;
      
      // Separador visual entre grupos
      &:not(:last-child)::after {
        content: '';
        position: absolute;
        bottom: -0.375rem;
        left: 0;
        right: 0;
        height: 1px;
        background-color: #dee2e6;
        margin: 0 1rem;
      }
      
      .nav-group-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.5rem 1rem;
        color: #495057;
        font-weight: 600;
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        transition: all 0.2s ease;
        border-radius: 0;
        margin-bottom: 0.25rem;
        background-color: #f8f9fa;
        border-left: 3px solid #007bff;
        width: 100%;
        
        &:hover {
          background-color: #e9ecef;
          color: #2c3e50;
        }
        
        &.active {
          background-color: #e3f2fd;
          color: #1976d2;
        }
        
        .nav-group-content {
          display: flex;
          align-items: center;
          flex: 1;
          
          .nav-icon {
            margin-right: 0.75rem;
            flex-shrink: 0;
          }
          
          .nav-text {
            flex: 1;
          }
        }
        
        .nav-arrow {
          transition: transform 0.2s ease;
          flex-shrink: 0;
        }
      }
      
      .submenu {
        background-color: #f8f9fa;
        border-left: 3px solid #007bff;
        margin-left: 0.5rem;
        margin-bottom: 0.25rem;
        
        .nav-item {
          .submenu-link {
            padding: 0.375rem 1rem 0.375rem 2rem;
            font-size: 0.8rem;
            color: #6c757d;
            
            &:hover {
              background-color: #e9ecef;
              color: #495057;
            }
            
            &.active {
              background-color: #007bff;
              color: #fff;
              font-weight: 500;
            }
            
            .submenu-icon {
              margin-right: 0.5rem;
              flex-shrink: 0;
            }
            
            .submenu-text {
              flex: 1;
            }
          }
        }
      }
    }
  }
}

// Transitions
.submenu-enter-active,
.submenu-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.submenu-enter,
.submenu-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.submenu-enter-to,
.submenu-leave {
  max-height: 500px;
  opacity: 1;
  transform: translateY(0);
}

// Responsive
@media (max-width: 1199.98px) {
  .vertical-nav-menu {
    transform: translateX(-100%);
    
    &:not(.collapsed) {
      transform: translateX(0);
    }
  }
}

// Scrollbar styling
.vertical-nav-menu::-webkit-scrollbar {
  width: 4px;
}

.vertical-nav-menu::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.vertical-nav-menu::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.vertical-nav-menu::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
